import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { useToast } from '../components/ui/Toast.tsx';
import {
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronUp,
  ShieldAlert,
  Mail,
  X,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type FieldType = 'text' | 'number' | 'boolean' | 'date' | 'email' | 'url' | 'select' | 'multiselect';

interface PropertyDefinition {
  id: string;
  key: string;
  label: string;
  fieldType: FieldType;
  isRequired: boolean;
  description: string | null;
  options: string[];
  validation: Record<string, unknown>;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
}

interface Suppression {
  id: string;
  email: string;
  reason: 'hard_bounce' | 'complaint' | 'manual';
  metadata: Record<string, unknown>;
  createdAt: string;
}

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'URL' },
  { value: 'select', label: 'Single Select' },
  { value: 'multiselect', label: 'Multi Select' },
];

const REASON_LABELS: Record<string, string> = {
  hard_bounce: 'Hard bounce',
  complaint: 'Complaint',
  manual: 'Manual',
};

const REASON_COLORS: Record<string, string> = {
  hard_bounce: 'bg-red-50 text-red-700 border-red-200',
  complaint: 'bg-amber-50 text-amber-700 border-amber-200',
  manual: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};

// ─── Property modal ───────────────────────────────────────────────────────────

interface PropertyModalProps {
  property?: PropertyDefinition;
  onClose: () => void;
  onSave: (data: Partial<PropertyDefinition> & { key?: string }) => void;
  saving: boolean;
}

function PropertyModal({ property, onClose, onSave, saving }: PropertyModalProps) {
  const isEdit = !!property;
  const [key, setKey] = useState(property?.key ?? '');
  const [label, setLabel] = useState(property?.label ?? '');
  const [fieldType, setFieldType] = useState<FieldType>(property?.fieldType ?? 'text');
  const [isRequired, setIsRequired] = useState(property?.isRequired ?? false);
  const [description, setDescription] = useState(property?.description ?? '');
  const [optionsRaw, setOptionsRaw] = useState((property?.options ?? []).join('\n'));

  const showOptions = fieldType === 'select' || fieldType === 'multiselect';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({
      key: isEdit ? undefined : key,
      label,
      fieldType,
      isRequired,
      description: description || undefined,
      options: showOptions ? optionsRaw.split('\n').map((s) => s.trim()).filter(Boolean) : [],
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h2 className="text-sm font-semibold text-zinc-900">
            {isEdit ? 'Edit property' : 'New property'}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {!isEdit && (
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1">
                Key <span className="text-zinc-400 font-normal">(snake_case, cannot be changed)</span>
              </label>
              <input
                required
                value={key}
                onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                placeholder="e.g. customer_tier"
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300 font-mono"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-zinc-700 mb-1">Label</label>
            <input
              required
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Customer Tier"
              className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-700 mb-1">Field type</label>
            <select
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value as FieldType)}
              className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300 bg-white"
            >
              {FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {showOptions && (
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1">
                Options <span className="text-zinc-400 font-normal">(one per line)</span>
              </label>
              <textarea
                value={optionsRaw}
                onChange={(e) => setOptionsRaw(e.target.value)}
                rows={4}
                placeholder="Option A&#10;Option B&#10;Option C"
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300 resize-none"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-zinc-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional — shown as hint to operators"
              className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300 resize-none"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              className="w-3.5 h-3.5 rounded"
            />
            <span className="text-xs text-zinc-700">Required field</span>
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function PropertyDefinitionsPage() {
  const [tab, setTab] = useState<'properties' | 'suppressions'>('properties');
  const qc = useQueryClient();
  const toast = useToast();

  // ── Properties state ────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<PropertyDefinition | undefined>(undefined);
  const [showInactive, setShowInactive] = useState(false);

  // ── Suppressions state ──────────────────────────────────────────────────
  const [suppressSearch, setSuppressSearch] = useState('');
  const [suppressPage, setSuppressPage] = useState(1);

  // ── Property queries ────────────────────────────────────────────────────
  const { data: properties = [], isLoading: propsLoading } = useQuery<PropertyDefinition[]>({
    queryKey: ['property-definitions', showInactive],
    queryFn: () =>
      api
        .get(`/api/v1/settings/properties${showInactive ? '?includeInactive=true' : ''}`)
        .then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<PropertyDefinition>) =>
      api.post('/api/v1/settings/properties', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-definitions'] });
      setShowModal(false);
      toast.success('Property created');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, data }: { key: string; data: Partial<PropertyDefinition> }) =>
      api.patch(`/api/v1/settings/properties/${key}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-definitions'] });
      setShowModal(false);
      setEditTarget(undefined);
      toast.success('Property updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) =>
      api.delete(`/api/v1/settings/properties/${key}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-definitions'] });
      toast.success('Property deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ key, isActive }: { key: string; isActive: boolean }) =>
      api.patch(`/api/v1/settings/properties/${key}`, { isActive }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['property-definitions'] }),
    onError: (err) => toast.error(extractError(err)),
  });

  const reorderMutation = useMutation({
    mutationFn: (items: { key: string; sortOrder: number }[]) =>
      api.put('/api/v1/settings/properties/reorder', { items }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['property-definitions'] }),
    onError: (err) => toast.error(extractError(err)),
  });

  // ── Suppression queries ─────────────────────────────────────────────────
  const { data: suppressData, isLoading: suppressLoading } = useQuery<{
    items: Suppression[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['email-suppressions', suppressPage, suppressSearch],
    queryFn: () =>
      api
        .get(
          `/api/v1/settings/suppressions?page=${suppressPage}&limit=50${suppressSearch ? `&search=${encodeURIComponent(suppressSearch)}` : ''}`,
        )
        .then((r) => r.data),
    enabled: tab === 'suppressions',
  });

  const removeSuppression = useMutation({
    mutationFn: (email: string) =>
      api.delete(`/api/v1/settings/suppressions/${encodeURIComponent(email)}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-suppressions'] });
      toast.success('Address removed from suppression list');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── Handlers ────────────────────────────────────────────────────────────
  function handleSave(data: Partial<PropertyDefinition> & { key?: string }) {
    if (editTarget) {
      updateMutation.mutate({ key: editTarget.key, data });
    } else {
      createMutation.mutate(data);
    }
  }

  function handleMoveUp(idx: number) {
    if (idx === 0) return;
    const swapped = properties.map((p, i) => {
      if (i === idx - 1) return { key: p.key, sortOrder: properties[idx].sortOrder };
      if (i === idx) return { key: p.key, sortOrder: properties[idx - 1].sortOrder };
      return { key: p.key, sortOrder: p.sortOrder };
    });
    reorderMutation.mutate(swapped);
  }

  function handleMoveDown(idx: number) {
    if (idx >= properties.length - 1) return;
    const swapped = properties.map((p, i) => {
      if (i === idx) return { key: p.key, sortOrder: properties[idx + 1].sortOrder };
      if (i === idx + 1) return { key: p.key, sortOrder: properties[idx].sortOrder };
      return { key: p.key, sortOrder: p.sortOrder };
    });
    reorderMutation.mutate(swapped);
  }

  function handleDelete(p: PropertyDefinition) {
    if (!confirm(`Delete property "${p.label}" (${p.key})? This cannot be undone.`)) return;
    deleteMutation.mutate(p.key);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Settings</h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            Manage custom customer properties and email suppression list
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200">
        {(
          [
            { id: 'properties', label: 'Custom Properties' },
            { id: 'suppressions', label: 'Email Suppressions' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-zinc-900 text-zinc-900'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Properties tab ── */}
      {tab === 'properties' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-zinc-600">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-3.5 h-3.5 rounded"
              />
              Show inactive
            </label>
            <button
              onClick={() => { setEditTarget(undefined); setShowModal(true); }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-zinc-900 text-white rounded-lg hover:bg-zinc-700 transition-colors"
            >
              <Plus size={14} />
              New property
            </button>
          </div>

          {propsLoading ? (
            <div className="text-sm text-zinc-400 py-8 text-center">Loading…</div>
          ) : properties.length === 0 ? (
            <div className="text-center py-16 text-zinc-400">
              <div className="text-sm">No custom properties defined yet.</div>
              <div className="text-xs mt-1">Click "New property" to add one.</div>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider w-8" />
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Label / Key</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Required</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {properties.map((p, idx) => (
                    <tr key={p.id} className="hover:bg-zinc-50/50">
                      <td className="px-2 py-3">
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => handleMoveUp(idx)}
                            disabled={idx === 0}
                            className="text-zinc-300 hover:text-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronUp size={13} />
                          </button>
                          <button
                            onClick={() => handleMoveDown(idx)}
                            disabled={idx === properties.length - 1}
                            className="text-zinc-300 hover:text-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronDown size={13} />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-zinc-900">{p.label}</div>
                        <div className="text-xs text-zinc-400 font-mono mt-0.5">{p.key}</div>
                        {p.description && (
                          <div className="text-xs text-zinc-400 mt-0.5 max-w-xs truncate">{p.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600">
                          {FIELD_TYPES.find((t) => t.value === p.fieldType)?.label ?? p.fieldType}
                        </span>
                        {(p.fieldType === 'select' || p.fieldType === 'multiselect') && p.options?.length > 0 && (
                          <div className="text-[10px] text-zinc-400 mt-0.5">
                            {p.options.slice(0, 3).join(', ')}
                            {p.options.length > 3 && ` +${p.options.length - 3} more`}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-500">
                        {p.isRequired ? (
                          <span className="text-xs font-medium text-red-600">Yes</span>
                        ) : (
                          <span className="text-xs text-zinc-400">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            toggleActiveMutation.mutate({ key: p.key, isActive: !p.isActive })
                          }
                          className="flex items-center gap-1.5 text-xs"
                        >
                          {p.isActive ? (
                            <>
                              <ToggleRight size={16} className="text-emerald-500" />
                              <span className="text-emerald-600">Active</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft size={16} className="text-zinc-300" />
                              <span className="text-zinc-400">Inactive</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => { setEditTarget(p); setShowModal(true); }}
                            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors"
                            title="Edit"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => handleDelete(p)}
                            className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Suppressions tab ── */}
      {tab === 'suppressions' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                value={suppressSearch}
                onChange={(e) => { setSuppressSearch(e.target.value); setSuppressPage(1); }}
                placeholder="Search by email…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300"
              />
            </div>
            <div className="text-sm text-zinc-500">
              {suppressData?.total ?? 0} suppressed address{suppressData?.total !== 1 ? 'es' : ''}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2.5 text-sm text-amber-800">
            <ShieldAlert size={15} className="mt-0.5 text-amber-500 flex-shrink-0" />
            <div>
              Addresses in this list are automatically skipped during campaign sends.
              Hard bounces and complaints are added automatically by SES.
              Remove an address only if you are certain the issue is resolved.
            </div>
          </div>

          {suppressLoading ? (
            <div className="text-sm text-zinc-400 py-8 text-center">Loading…</div>
          ) : !suppressData?.items.length ? (
            <div className="text-center py-16 text-zinc-400">
              <div className="text-sm">No suppressed addresses.</div>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Added</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {suppressData.items.map((s) => (
                    <tr key={s.id} className="hover:bg-zinc-50/50">
                      <td className="px-4 py-3 font-mono text-xs text-zinc-700">{s.email}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${REASON_COLORS[s.reason]}`}
                        >
                          {REASON_LABELS[s.reason]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500">
                        {new Date(s.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => {
                            if (confirm(`Remove ${s.email} from suppression list?`)) {
                              removeSuppression.mutate(s.email);
                            }
                          }}
                          className="text-xs text-zinc-400 hover:text-red-600 px-2 py-1 hover:bg-red-50 rounded-md transition-colors"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination */}
              {suppressData.total > suppressData.limit && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-100 text-xs text-zinc-500">
                  <span>
                    {((suppressData.page - 1) * suppressData.limit) + 1}–
                    {Math.min(suppressData.page * suppressData.limit, suppressData.total)} of {suppressData.total}
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={suppressPage === 1}
                      onClick={() => setSuppressPage((p) => p - 1)}
                      className="px-2 py-1 border border-zinc-200 rounded hover:bg-zinc-50 disabled:opacity-40"
                    >
                      Prev
                    </button>
                    <button
                      disabled={suppressData.page * suppressData.limit >= suppressData.total}
                      onClick={() => setSuppressPage((p) => p + 1)}
                      className="px-2 py-1 border border-zinc-200 rounded hover:bg-zinc-50 disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Property modal */}
      {showModal && (
        <PropertyModal
          property={editTarget}
          onClose={() => { setShowModal(false); setEditTarget(undefined); }}
          onSave={handleSave}
          saving={isSaving}
        />
      )}
    </div>
  );
}
